import { DefineDevice, KK, VirtualMG63, VirtualMG75V2C12, VirtualRA68_1, VK } from '@hid/define/define'
import { keys } from '@hid/define/define_keys'
import { mg63FnMatrixIndexToKK, mg75v2MatrixIndexToKK, ra68FnMatrixIndexToKK } from '../../mg63'

const huochairenFnMatrixIndexToKKByDevice = new Map<DefineDevice, { [index: number]: number }>([
  [VirtualMG63, mg63FnMatrixIndexToKK],
  [VirtualRA68_1, ra68FnMatrixIndexToKK],
  [VirtualMG75V2C12, mg75v2MatrixIndexToKK],
])

const huochairenFnVkByDvkCache = new Map<DefineDevice, ReadonlyMap<number, VK>>()
const huochairenDefaultFnSupportedDevices = new Set<DefineDevice>([VirtualMG63, VirtualRA68_1, VirtualMG75V2C12])

const huochairenDefaultFnBaseline = new Map<KK, VK>([
  [0x1b as KK, 0xe108 as VK],
  [0x4f as KK, 0xe103 as VK],
  [0x50 as KK, 0xe102 as VK],
  [0x51 as KK, 0xe100 as VK],
  [0x52 as KK, 0xe101 as VK],
  [0xe3 as KK, 0xe10b as VK],
])

const huochairenDefaultFnOverrideByDevice = new Map<DefineDevice, ReadonlyMap<KK, VK>>([
  [
    VirtualMG63,
    new Map<KK, VK>([
      [0x1e as KK, 0x0070 as VK],
      [0x1f as KK, 0x0071 as VK],
      [0x20 as KK, 0x0072 as VK],
      [0x21 as KK, 0x0073 as VK],
      [0x22 as KK, 0x0074 as VK],
      [0x23 as KK, 0x0075 as VK],
      [0x24 as KK, 0x0076 as VK],
      [0x25 as KK, 0x0077 as VK],
      [0x26 as KK, 0x0078 as VK],
      [0x27 as KK, 0x0079 as VK],
      [0x29 as KK, 0x00c0 as VK],
      [0x2d as KK, 0x007a as VK],
      [0x2e as KK, 0x007b as VK],
      [0x2f as KK, 0xe104 as VK],
      [0x30 as KK, 0xe105 as VK],
      [0x31 as KK, 0xe12c as VK],
    ]),
  ],
  [
    VirtualRA68_1,
    new Map<KK, VK>([
      [0x04 as KK, 0xe109 as VK],
      [0x16 as KK, 0xe10a as VK],
      [0x1e as KK, 0x0070 as VK],
      [0x1f as KK, 0x0071 as VK],
      [0x20 as KK, 0x0072 as VK],
      [0x21 as KK, 0x0073 as VK],
      [0x22 as KK, 0x0074 as VK],
      [0x23 as KK, 0x0075 as VK],
      [0x24 as KK, 0x0076 as VK],
      [0x25 as KK, 0x0077 as VK],
      [0x26 as KK, 0x0078 as VK],
      [0x27 as KK, 0x0079 as VK],
      [0x29 as KK, 0x00c0 as VK],
      [0x2d as KK, 0x007a as VK],
      [0x2e as KK, 0x007b as VK],
      [0x2f as KK, 0xe104 as VK],
      [0x30 as KK, 0xe105 as VK],
      [0x31 as KK, 0xe12c as VK],
    ]),
  ],
  [
    VirtualMG75V2C12,
    new Map<KK, VK>([
      [0x04 as KK, 0xe109 as VK],
      [0x16 as KK, 0xe10a as VK],
      [0x3a as KK, 0x00b6 as VK],
      [0x3b as KK, 0x00ac as VK],
      [0x3c as KK, 0x00b4 as VK],
      [0x3d as KK, 0x00b5 as VK],
      [0x3e as KK, 0x00b1 as VK],
      [0x3f as KK, 0x00b0 as VK],
      [0x40 as KK, 0x00b3 as VK],
      [0x41 as KK, 0x00b2 as VK],
      [0x42 as KK, 0x00af as VK],
      [0x43 as KK, 0x00ae as VK],
      [0x44 as KK, 0x00ad as VK],
      [0x45 as KK, 0x00b7 as VK],
      [0x4b as KK, 0xe104 as VK],
      [0x4c as KK, 0xe10f as VK],
      [0x4e as KK, 0xe105 as VK],
    ]),
  ],
])

const getDefaultHuochairenFnMatrixIndexToKK = () => {
  return huochairenFnMatrixIndexToKKByDevice.get(VirtualMG63) as { [index: number]: number }
}

export const getHuochairenFnMatrixIndexToKKMap = (deviceID: DefineDevice) => {
  return huochairenFnMatrixIndexToKKByDevice.get(deviceID) ?? getDefaultHuochairenFnMatrixIndexToKK()
}

export const getHuochairenFnVkByDvk = (deviceID: DefineDevice): ReadonlyMap<number, VK> => {
  const cached = huochairenFnVkByDvkCache.get(deviceID)
  if (cached !== undefined) {
    return cached
  }

  const dvkToVk = new Map<number, VK>()
  keys.forEach((key) => {
    const dvk = key.DVKs.get(deviceID)
    if (dvk !== undefined) {
      dvkToVk.set(dvk, key.VirtualKey)
    }
  })

  huochairenFnVkByDvkCache.set(deviceID, dvkToVk)
  return dvkToVk
}

export const hasHuochairenDefaultFnLayout = (deviceID: DefineDevice): boolean => {
  return huochairenDefaultFnSupportedDevices.has(deviceID)
}

export const getHuochairenDefaultFnKeyMap = (deviceID: DefineDevice): Map<KK, VK> | undefined => {
  if (!hasHuochairenDefaultFnLayout(deviceID)) return undefined
  const result = new Map<KK, VK>(huochairenDefaultFnBaseline)
  const override = huochairenDefaultFnOverrideByDevice.get(deviceID)
  if (override) {
    override.forEach((vk, kk) => result.set(kk, vk))
  }
  return result
}

export const getHuochairenDefaultFnVk = (kk: KK, deviceID: DefineDevice): VK | undefined => {
  if (!hasHuochairenDefaultFnLayout(deviceID)) {
    return undefined
  }

  const override = huochairenDefaultFnOverrideByDevice.get(deviceID)
  return override?.get(kk) ?? huochairenDefaultFnBaseline.get(kk)
}
